import { Component } from 'react'
import { ReduxFormProps, StoreDispatch } from 'interfaces/component'
import styles from 'components/features/pfm/SimilarTransactionsForm/SimilarTransactionsForm.styl'
import { SimilarTransactionFormValues } from 'interfaces/form'
import { State } from 'interfaces/state'
import { connect } from 'react-redux'
import reduce from 'lodash/reduce'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { Transaction, TransactionUid } from 'interfaces/moneyflow'
import Button from 'components/features/pfm/Button/Button'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { TransactionRecatFloatingPane } from 'interfaces/floatingPanes'
import { reduxForm, Field, change, getFormInitialValues, getFormValues } from 'redux-form'
import { FORM_SIMILAR_TRANSACTIONS, FORM_RECAT_TRANSACTION } from 'constants/forms'
import CheckboxWrapped from 'components/atoms/inputs/CheckboxWrapped'
import { getItemsFromCollection, getItemFromCollection } from 'helpers/normalizeHelpers'
import { TransactionNarrow } from 'components/features/pfm/TransactionNarrow/TransactionNarrow'
import { AnyDict } from 'interfaces'
import { isFetchableLoaded } from 'helpers/loadableHelpers'
import { TypedMoneyflowCategory } from 'interfaces/meta'

type SimilarTransactionsStateProps =
  & ReduxFormProps

type SimilarTransactionsDispatchProps =
  & Pick<ReduxFormProps, 'onSubmit'>
  & {
    toggleSelectAll: (nextValue: boolean) => void
    closePane: () => void
  }

type SimilarTransactionsOwnProps =
  & TransactionRecatFloatingPane
  & {
    recatApplyToAll: boolean
    category: TypedMoneyflowCategory | null
  }

type SimilarTransactionsProps =
  & SimilarTransactionsStateProps
  & SimilarTransactionsDispatchProps
  & SimilarTransactionsOwnProps

@reduxForm({
  form: FORM_SIMILAR_TRANSACTIONS,
  enableReinitialize: true,
})
class SimilarTransactionsFormComponent extends Component<SimilarTransactionsProps, {}> {

  render () {
    const {
      similarTransactions,
      transaction,
      category,
      handleSubmit,
      toggleSelectAll,
      closePane,
    } = this.props

    if (!isFetchableLoaded(similarTransactions) || !transaction || !category) {
      return null
    }

    const checkboxRows = getItemsFromCollection(similarTransactions.data).map(({ uid: transactionUid }: Transaction) => {
      const similarTransaction = getItemFromCollection(similarTransactions.data, transactionUid)

      return !!similarTransaction && (
        <li
          key={ transactionUid }
          className={ styles.checkboxRow }
        >
          <Field
            type='checkbox'
            name={ `items.${ transactionUid }` }
            component={ CheckboxWrapped }
            labelRenderer={
              (isChecked: boolean) => (
                <div className={ styles.transactionWrapper }>
                  <TransactionNarrow
                    transaction={ similarTransaction }
                    newCategory={ isChecked ? category : undefined }
                    disableRecat
                    hideEdit
                  />
                </div>
              )
            }
          />
        </li>
      )
    })

    const header = (
      <h3 className={ styles.title }>
        Select <strong className={ styles.categoryName }>{ category.value }</strong> transactions
      </h3>
    )

    const footer = (
      <>
        <fieldset className={ styles.checkboxes }>
          <Field
            name='select_all'
            type='checkbox'
            label='Select All'
            qaMarker='select-all-transactions'
            component={ CheckboxWrapped }
            onChange={ (_e: any, nextValue: boolean) => toggleSelectAll(nextValue) }
          />
        </fieldset>
        <Button
          size='medium'
          text='Done'
          fullWidth
          submit
        />
      </>
    )

    return (
      <form onSubmit={ handleSubmit }>
        <FloatingPaneLayout
          type='transaction-recat'
          header={ header }
          footer={ footer }
          onClose={ closePane }
        >
          <ul>
            { checkboxRows }
          </ul>
        </FloatingPaneLayout>
      </form>
    )
  }
}

const reduceTransactionUids = (uids: TransactionUid[]): AnyDict => {
  return uids.reduce<AnyDict>(
    (acc, uid) => {
      acc[uid] = true
      return acc
    },
    {}
  )
}


const mapStateToProps = (state: State, { selectedTransactionUids, similarTransactions }: SimilarTransactionsOwnProps): SimilarTransactionsStateProps => {
  const initialValues = getFormInitialValues(FORM_SIMILAR_TRANSACTIONS)(state)
  const similarTransactionsCount = isFetchableLoaded(similarTransactions) ? similarTransactions.data.allKeys.length : 0
  if (initialValues) {
    // form initialized
    const values = getFormValues<SimilarTransactionFormValues>(FORM_SIMILAR_TRANSACTIONS)(state)
    const selectedTransactionsCount = reduce<boolean, number>(
      values.items,
      (acc, value) => value ? acc + 1 : acc,
      0
    )

    const nextValues: SimilarTransactionFormValues = {
      items: values.items,
      select_all: similarTransactionsCount === selectedTransactionsCount,
    }

    return {
      initialValues: nextValues,
    }
  } else {
    // form is not initialized
    const selectAllValue = selectedTransactionUids.length === similarTransactionsCount
    const itemsValue = selectedTransactionUids ? reduceTransactionUids(selectedTransactionUids) : []
    const nextInitialValues: SimilarTransactionFormValues = {
      items: itemsValue,
      select_all: selectAllValue,
    }
    return {
      initialValues: nextInitialValues,
    }
  }
}

const mapDispatchToProps = (dispatch: StoreDispatch, ownProps: SimilarTransactionsOwnProps): SimilarTransactionsDispatchProps => {
  const { similarTransactions, recatApplyToAll } = ownProps

  return {
    toggleSelectAll: (nextSelectAllValue: boolean) => {
      let nextItemsValue = {}
      if (nextSelectAllValue) {
        const uids = isFetchableLoaded(similarTransactions) ? similarTransactions.data.allKeys : []
        nextItemsValue = reduceTransactionUids(uids)
      }
      dispatch(change(FORM_SIMILAR_TRANSACTIONS, 'items', nextItemsValue))
    },
    closePane: () => {
      dispatch(modifyFloatingPaneActionCreator('transaction-recat', { mode: 'category' }))
    },
    onSubmit: (values: SimilarTransactionFormValues) => {
      if (!isFetchableLoaded(similarTransactions)) {
        return {}
      }

      const selectedTransactionUids: TransactionUid[] = reduce<boolean, TransactionUid[]>(values.items, (acc, value, key) => {
        if (!value) {
          return acc
        }

        return [
          ...acc,
          key,
        ]

      }, [])

      dispatch(modifyFloatingPaneActionCreator('transaction-recat', { mode: 'category', selectedTransactionUids }))
      let recatApplyToAllNextValue: boolean | null = null
      if (recatApplyToAll && !selectedTransactionUids.length) {
        recatApplyToAllNextValue = false
      }
      if (!recatApplyToAll && selectedTransactionUids.length === similarTransactions.data.allKeys.length) {
        recatApplyToAllNextValue = true
      }
      if (recatApplyToAllNextValue !== null) {
        dispatch(change(FORM_RECAT_TRANSACTION, 'apply_to_all', recatApplyToAllNextValue))
        dispatch(change(FORM_RECAT_TRANSACTION, 'create_rule', recatApplyToAllNextValue))
      }

      return {}
    },
  }
}

export const SimilarTransactionsForm = connect(mapStateToProps, mapDispatchToProps)(SimilarTransactionsFormComponent)
